/*
	* Handles all state relating to the react-map-gl component: location, map options/properties, viewport, etc
	*
 */

import { replace } from 'connected-react-router'
import {FlyToInterpolator} from "react-map-gl";

import { easeQuadInOut } from 'd3-ease'
import config from "../../content/config";
// current story map, example reference here, in the future need to pass in from elsewhere (eg server call)
import sampleStorymap from "../../content/storymaps/oz-dashboard-storymap";
import MAPSTYLE_BASIC from "../../content/mapbox-style-basic";
import MAPSTYLE_DETAIL from "../../content/mapbox-style-detail";
import MAPSTYLE_SATELLITE from "../../content/mapbox-style-satellite";
import {
	BASE_MAP_LAYERS,
	COUNTY_ZOOM_TO_LEVEL,
	MAPBOX_SATELLITE_LAYER_ID,
	GEOGRAPHY_OPTIONS,
	MAP_PAN_TRANSITION_DURATION,
	STATE_MASK_ID, STATE_ZOOM_TO_LEVEL
} from "../../constants"


import axios from "axios";
import DataManager from "../../DataManagers/DataManager";
import {tableWithSameValue} from "../../utils";
import TurfManager from "../../Turf/TurfManager";
import AdminBoundsDataManager from "../../DataManagers/AdminBoundsDataManager";
import {LAT_LON_FOR_US_STATES} from "../../DataManagers/utilsColorTheme";
import {_setStylingForVisualizedDataLayer, getMapboxFilters} from "./utilsMapboxLayerPaint";
import {
	currentSceneIndexSelector,
	cartoLayerIdOfSelectedGeographySelector,
	mapboxLayersSelector,
	mapSelector,
	selectedGeographySelector,
	selectedRegionInfoSelector,
	selectedUSStatesToFilterWithinSelector,
	showFiltersOnMapSelector,
	showPrimaryFilterSelector,
	fieldFilterBoundsSelector,
	showStartScreenSelector,
	fieldExtentsSelector,
	selectedRegionACSDataRowSelector,
	_isTransitioningSelector,
	initialLoadOfMapHasCompletedSelector,
	selectedRuralDefinitionSelector,
	comparisonRegionInfoSelector,
	userSetHideLayersSelector,
	numActiveFiltersSelector,
	_mapRefSelector,
	hoveredLayersInfoSelector,
} from "../selectors";
import {
	initializeCartoACSDataFromURL,
	addUSStateToFilterWithin,
	defaultUSStatesToFilterWithin,
	fetchCartoACSSummaryAndDataIfNeeded,
	changeSelectedGeography,
	getCartoGeoIdForLatLon,
	updateFieldsBoundsAfterYamlSpecifiedFilterButtonPress, getBoundForStates, updateUSStatesToFilterWithinAction
} from "./cartoACSDataActions";
import {
	primaryFilterSelector} from "../selectors";
import {initializeMapboxLayersFromURL} from "./mapboxLayersActions";
import {applyAllFiltersSelector, smallReduxStateForURLSelector} from "../selectors";
import {openOption} from "./uiActions";
import {getGeoIdForSelectedRegion, isARuralDefinition, isSignificantlyDifferent} from "../../utilsData";
import {recordSceneChangeAsPageviewInGA} from "../../googleAnalytics";

const VIEWPORT_TRANSITION_PROPERTIES = {
	transitionInterpolator: new FlyToInterpolator(),
	transitionEasing: easeQuadInOut,
	transitionDuration: MAP_PAN_TRANSITION_DURATION,
}
const LOCATION_MARKER_ICON_ID = 'locationMarkerIcon'
const geojsonForLocationMarker = {
	"type": "FeatureCollection",
	"features": [{
		"type": "Feature",
		"geometry": {
			"type": "Point",
			"coordinates": [0, 0]
		}
	}]
};
const locationMarkerLayerJSON = {
	"id": "point",
	"type": "symbol",
	"source": LOCATION_MARKER_ICON_ID,
	"layout": {
		"icon-image": "location-marker",
		"icon-size": 0.1,
		"icon-allow-overlap": true,
		"icon-ignore-placement": true,
		"symbol-z-order": "source",
		"icon-anchor": "bottom" // so the tip sits on the location
	}
}

const getReduxStateFromURL = location => {
	let hashLessHashExtension = decodeURIComponent(location.hash.slice(1)).replace('#','')
	if (hashLessHashExtension[0] === '/') {
		hashLessHashExtension = hashLessHashExtension.replace('/', '')
	}
	return (location.hash && location.hash.length > 3) ? JSON.parse(hashLessHashExtension) : {}
}


// ACTION OBJECT CREATORS: *****************************
// - do nothing but return action objects
// - correspond 1-to-1 to redux switch cases
// - are named as commands describing what should be done (not how it's done), ie "declarative"

export const UPDATE_MAP_STATE_ACTION = 'UPDATE_MAP_STATE_ACTION'
export const updateMapStateAction = (stateUpdates) => ({
	type: UPDATE_MAP_STATE_ACTION,
	stateUpdates: stateUpdates
})

export const INITIALIZE_MAP_FROM_URL = 'INITIALIZE_MAP_FROM_URL'
const initializeMapFromURLAction = (stateUpdates) => ({
	type: INITIALIZE_MAP_FROM_URL,
	stateUpdates: stateUpdates,
})

export const INITIAL_LOAD_OF_MAP_HAS_COMPLETED_ACTION = 'INITIAL_LOAD_OF_MAP_HAS_COMPLETED_ACTION'
const initialLoadOfMapHasCompletedAction = () => ({
	type: INITIAL_LOAD_OF_MAP_HAS_COMPLETED_ACTION,
})

export const SAVE_RECALCULATED_FIELD_EXTENTS_ACTION = 'SAVE_RECALCULATED_FIELD_EXTENTS_ACTION'
// export const saveRecalculatedFieldExtentsAction = recalculatedFieldExtents => ({
// 	type: SAVE_RECALCULATED_FIELD_EXTENTS_ACTION,
// 	recalculatedFieldExtents,
// })

export const TOGGLE_BUTTON_TOGGLE_STATUS_ARRAY_FOR_FIELD_ACTION = 'TOGGLE_BUTTON_TOGGLE_STATUS_ARRAY_FOR_FIELD_ACTION'
export const toggleButtonToggleStatusArrayForFieldAction = (fieldName, buttonIndex) => ({
	type: TOGGLE_BUTTON_TOGGLE_STATUS_ARRAY_FOR_FIELD_ACTION,
	fieldName,
	buttonIndex,
})

export const SET_BUTTON_TOGGLE_STATUS_ARRAY_FOR_FIELD_ACTION = 'SET_BUTTON_TOGGLE_STATUS_ARRAY_FOR_FIELD_ACTION'
export const seedButtonToggleStatusArrayForFieldAction = ({fieldName, buttonIsActiveOnSceneStart}) => ({
	type: SET_BUTTON_TOGGLE_STATUS_ARRAY_FOR_FIELD_ACTION,
	fieldName,
	buttonIsActiveOnSceneStart,
})

export const TOGGLE_HAS_BEEN_EDITED_BY_THE_USER_PROP_ACTION = 'TOGGLE_HAS_BEEN_EDITED_BY_THE_USER_PROP_ACTION'
export const toggleHasBeenEditedByTheUserPropAction = (fieldName, setHasBeenEditedToThisBoolValue) => ({
	type: TOGGLE_HAS_BEEN_EDITED_BY_THE_USER_PROP_ACTION,
	fieldName,
	setHasBeenEditedToThisBoolValue,
})

export const CLEAR_ALL_FILTERS_ACTION = 'CLEAR_ALL_FILTERS_ACTION'
const clearAllFiltersAction = () => ({
	type: CLEAR_ALL_FILTERS_ACTION,
})

export const UPDATE_SELECTED_AND_COMPARISON_REGIONS_TO_NEW_GEOGRAPHY = 'UPDATE_SELECTED_AND_COMPARISON_REGIONS_TO_NEW_GEOGRAPHY'
export const updateSelectedRegionToNewGeographyAction = ({geoIdForSelectedRegion, geoIdForSelectedRegion_comparison}) => ({
	type: UPDATE_SELECTED_AND_COMPARISON_REGIONS_TO_NEW_GEOGRAPHY,
	geoIdForSelectedRegion,
})

// INTERNAL HELPER ACTIONS (not exported to UI) *****************************

export const saveFieldExtentsFromCarto = (res) => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const mapState = mapSelector(reduxState)
		
		const stateUpdates = {
			dynamicMapboxFilters: {
				...mapState.dynamicMapboxFilters,
				fieldExtentsCache: DataManager.stats.formatFieldExtentsFromCartQuery(res)
			}
		}
		dispatch(updateMapStateAction(stateUpdates))
	}
}

const isVisible = (layer, visiblityLookup, cartoLayerIdOfSelectedGeography, selectedRuralDefinition, showFiltersOnMap, showStartScreen, _isTransitioning) => {
	const id = layer.get('id')
	
	if (id === STATE_MASK_ID && !_isTransitioning) return true // Needed for Turf bounding-box zooming
	
	const isACartoLayer = DataManager.getCartoDataLayers().includes(id)
	if (isACartoLayer && (showStartScreen || _isTransitioning)) {
		return false
	}
	
	const isALocalAssetLayer = DataManager.getCartoLocalAssetDataLayers().includes(id)
	if (isALocalAssetLayer) {
		return !showFiltersOnMap
	}
	
	const isAnACSAttributeLayer = Object.values(GEOGRAPHY_OPTIONS).map(({cartoLayerId}) => cartoLayerId).includes(id)
	if (isAnACSAttributeLayer) {
		return id === cartoLayerIdOfSelectedGeography
	}
	
	if (isARuralDefinition(id)) {
		return id === selectedRuralDefinition
	}
	
	const isSatelliteLayer = (id === MAPBOX_SATELLITE_LAYER_ID)
	const isVisibleLayer = id => visiblityLookup[id] === true
	const isNonSatelliteBaseMap = id => (visiblityLookup[id] === undefined) && !isSatelliteLayer
	return isVisibleLayer(id) || isNonSatelliteBaseMap(id)
}

// Note that, when this function is called during a sceneUpdate, this it's run *before* the state is updated,
// so we need to manually provide the pending state updates, as necessary.
const _getNewMapStyleAndVisibleLayers = ({newVisibilities = {}, _newDefaultMapStyle, newSceneIndex, reduxState}) => {
	const selectedUSStatesToFilterWithin = selectedUSStatesToFilterWithinSelector(reduxState)
	const mapboxLayersReduxState = mapboxLayersSelector(reduxState)
	const mapState = mapSelector(reduxState)
	const cartoLayerIdOfSelectedGeography = cartoLayerIdOfSelectedGeographySelector(reduxState)
	const selectedRuralDefinition = selectedRuralDefinitionSelector(reduxState)
	const primaryFilter = primaryFilterSelector(reduxState)
	const applyAllFilters = applyAllFiltersSelector(reduxState)
	const showFiltersOnMap = showFiltersOnMapSelector(reduxState)
	const showStartScreen = showStartScreenSelector(reduxState)
	const _isTransitioning = _isTransitioningSelector(reduxState)
	
	const fieldFilterBounds = fieldFilterBoundsSelector(reduxState)
	const selectedRegionInfo = selectedRegionInfoSelector(reduxState)
	const comparisonRegionInfo = comparisonRegionInfoSelector(reduxState)
	const userSetHideLayers = userSetHideLayersSelector(reduxState)
	

	const _newSceneIndex = newSceneIndex !== undefined ? newSceneIndex : mapState.currentSceneIndex
	const visibleLayers = {...mapState.visibleLayers, ...newVisibilities};
	const _defaultMapStyle = _newDefaultMapStyle || mapState.defaultMapStyle

	const allLayers = _defaultMapStyle.get('layers')
		.filter(layer => isVisible(layer, visibleLayers, cartoLayerIdOfSelectedGeography, selectedRuralDefinition, showFiltersOnMap, showStartScreen, _isTransitioning))
		.map(layer => _setStylingForVisualizedDataLayer(layer, _newSceneIndex, selectedRegionInfo, comparisonRegionInfo, fieldFilterBounds, primaryFilter, showFiltersOnMap, mapboxLayersReduxState, cartoLayerIdOfSelectedGeography, selectedUSStatesToFilterWithin, applyAllFilters, showStartScreen))

	const activeLayers = allLayers.filter(layer => {
		const id = layer.get('id')
		return (!(id in userSetHideLayers) || userSetHideLayers[id] === false)
	})

	//console.log('Num visible layers:', activeLayers.size)
	return {
		mapStyle: _defaultMapStyle.set('layers', activeLayers),
		visibleLayers: visibleLayers,
		activeMapboxFilters: getMapboxFilters(activeLayers, _newSceneIndex), // used for UI, legend, etc,
		allMapboxFilters: getMapboxFilters(allLayers, _newSceneIndex)
	}
}

const _onLoadStorymap = (rawStorymap) => {
	return (dispatch, getReduxState) => {
		const stateUpdates = {
			rawStorymap: rawStorymap,
		}

		dispatch(updateMapStateAction(stateUpdates))

		// Start loading the mapbox styles
		if (config.LOAD_MAPBOX_STYLES_FROM_REMOTE) {
			dispatch(_loadMapboxStyles(rawStorymap))
		} else {
			dispatch(_onLoadMapboxStyles(MAPSTYLE_BASIC, MAPSTYLE_DETAIL, MAPSTYLE_SATELLITE)) // load local
		}
	}
}

const _loadMapboxStyles = (rawStorymap) => {
	return (dispatch, getReduxState) => {
		// entries for each mapbox style type and reading corresponding key from storymap specs
		const styleKeys = [
			{type: BASE_MAP_LAYERS.BASIC, key: rawStorymap.MAPSTYLE_BASIC},
			{type: BASE_MAP_LAYERS.DETAILED, key: rawStorymap.MAPSTYLE_DETAIL},
			{type: BASE_MAP_LAYERS.SATELLITE, key: rawStorymap.MAPSTYLE_SATELLITE}
		]

		// create promises and batch request
		const promises = styleKeys.filter(sk => sk.key !== undefined)
			.map(sk => axios.create({method: 'get', url: `https://api.mapbox.com/styles/v1/${rawStorymap.MAPBOX_USER}/${sk.key}?access_token=${config.MAPBOX_TOKEN}`})
				.request({mapboxType: sk.type})
			)

		axios.all(promises).then(responses => {
			// convert the responses to lookup based on type
			const results = responses.reduce((res, r) => {
				res[r.config.mapboxType] = r.data
				return res
			}, {})

			// default to local if not specified
			const styles = {
				basic: (BASE_MAP_LAYERS.BASIC in results) ? results[BASE_MAP_LAYERS.BASIC] : MAPSTYLE_BASIC,
				detail: (BASE_MAP_LAYERS.DETAILED in results) ? results[BASE_MAP_LAYERS.DETAILED] : MAPSTYLE_DETAIL,
				satellite: (BASE_MAP_LAYERS.SATELLITE in results) ? results[BASE_MAP_LAYERS.SATELLITE] : MAPSTYLE_SATELLITE
			}
			dispatch(_onLoadMapboxStyles(styles.basic, styles.detail, styles.satellite))
		}).catch( err => {
			// print out error and load local
			//console.log(err)
			dispatch(_onLoadMapboxStyles(MAPSTYLE_BASIC, MAPSTYLE_DETAIL, MAPSTYLE_SATELLITE))
		})
	}
}

const tryAddingMapboxSatelliteSource = (mapStyle) => {
	const sat_id = "mapbox://mapbox.satellite"
	const sat_val = {
        "tileSize": 256,
        "type": "raster",
        "url": "mapbox://mapbox.satellite"
    }

	if (sat_id in mapStyle.sources) {
		return mapStyle
	} else {
		return {
			...mapStyle,
			sources: {
				...mapStyle.sources,
				[sat_id]: sat_val
			}
		}
	}
}

const _onLoadMapboxStyles = (basicMapStyle, detailedMapStyle, satelliteMapStyle) => {
	return (dispatch, getReduxState) => {
		const mapState = getReduxState().map

		basicMapStyle = tryAddingMapboxSatelliteSource(basicMapStyle)
		detailedMapStyle = tryAddingMapboxSatelliteSource(detailedMapStyle)

		// init data manager with content
		DataManager.initWithContent({
			storymap: mapState.rawStorymap,
			basicMapStyle: basicMapStyle,
			detailedMapStyle: detailedMapStyle,
			satelliteMapStyle: satelliteMapStyle
		})

		// finish
		dispatch(_finishInitialization())
	}
}

const _finishInitialization = () => {
	return (dispatch, getReduxState) => {
		const mapState = getReduxState().map

		const mapStyle = (mapState.viewport.zoom > DataManager.getZoomLevelToSwapBasemaps()) ? DataManager.detailedMapStyle() : DataManager.defaultMapStyle()

		const stateUpdates = {
			visibleLayers: tableWithSameValue(DataManager.getLayerNames(), false), // all layers not visible
			mapStyle: mapStyle,
			defaultMapStyle: mapStyle,
			viewport: {
				// If we're loading from a URL state in which the user has dragged away from the storymap, don't apply storymap viewport
				...mapState.viewport,
				...(mapState.userHasDraggedToNewLocation ? {} : DataManager.getStorySceneAtIndex(mapState.currentSceneIndex).viewport),
				...VIEWPORT_TRANSITION_PROPERTIES,
			},
			_prev: { zoom: mapState.viewport.zoom }, // update zoom prev
			showStartScreen: mapState.showStartScreen && !DataManager.shouldSkipIntro()
		}

		if (DataManager.hasPredefinedStartingLocation()) {
			const selectedRegionInfo = DataManager.getPredefinedStartingLocation()
			dispatch(defaultUSStatesToFilterWithin([selectedRegionInfo.stateName]))

			stateUpdates.selectedRegionInfo = selectedRegionInfo
		}

		dispatch(updateMapStateAction(stateUpdates))

		// add the turfManager too after mapRef is created
		dispatch(updateMapStateAction({
			_turfManager: new TurfManager(_getMap(mapState), DataManager.getLayerNames())
		}))

		// after setting the state
		dispatch(_changeScene(mapState.currentSceneIndex)) // update visibility of layers

		// refresh the map
		dispatch(resize())
	}
}

const _potentiallySwapBasemaps = (newZoomLevel) => {
	return (dispatch, getReduxState) => {
		const mapState = getReduxState().map
		const { _prev } = mapState
		const z = DataManager.getZoomLevelToSwapBasemaps()

		if (_prev.zoom > 0) {
			if ((newZoomLevel <= z && _prev.zoom > z) || (newZoomLevel > z && _prev.zoom <= z)) {
				if (newZoomLevel > z) { // remember: zoomed in (detailed) means bigger
					DataManager.swapBaseLayers(_getMap(mapState), BASE_MAP_LAYERS.BASIC, BASE_MAP_LAYERS.DETAILED)
				} else {
					DataManager.swapBaseLayers(_getMap(mapState), BASE_MAP_LAYERS.DETAILED, BASE_MAP_LAYERS.BASIC)
				}
			}
		}
		// update prev reference
		dispatch(updateMapStateAction({
			_prev: {
				..._prev,
				zoom: newZoomLevel
			}
		}))
	}
}

const _getMap = ({_mapRef}) => {
	return (_mapRef && _mapRef.current) ? _mapRef.current.getMap() : null;
}

export const mapFitBounds = (bounds, padding = 30) => {
	return (dispatch, getReduxState) => {
		const mapState = getReduxState().map
		const mapboxMap = _getMap(mapState)

		const {center, zoom} = mapboxMap.cameraForBounds(bounds, {padding:{top:padding, bottom:padding, left:padding, right:padding}})
		const changeTriggeredByAutoBoundingBox = true
		dispatch(_onViewportChange({latitude: center.lat, longitude: center.lng, zoom: zoom}, changeTriggeredByAutoBoundingBox))
	}
}

export const toggleButtonToggleStatusArrayForField = ({fieldName,  buttonIsActiveOnSceneStart}, buttonIndex) => {
	return (dispatch, getReduxState) => {
		dispatch(toggleButtonToggleStatusArrayForFieldAction(fieldName, buttonIndex))
		dispatch(updateShowPrimaryFilter())
	}
}

export const toggleHasBeenEditedByTheUserProp = (fieldName, setHasBeenEditedToThisBoolValue = undefined) => {
	return (dispatch, getReduxState) => {
		dispatch(toggleHasBeenEditedByTheUserPropAction(fieldName, setHasBeenEditedToThisBoolValue))
		dispatch(updateShowPrimaryFilter())
		dispatch(_updateMapStyleOnVisibilityChange())
	}
}

export const clearAllFilters = () => {
	return (dispatch, getReduxState) => {
		dispatch(clearAllFiltersAction())
		dispatch(_updateMapStyleOnVisibilityChange())
	}
}

const _changeScene = (newIndex, additionalStateUpdates = {}, resetToStorymapLocation = false) => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const mapState = mapSelector(reduxState)
		const showStartScreen = showStartScreenSelector(reduxState)
		const { selectedRegionInfo } = additionalStateUpdates
		const __userHasDraggedToNewLocation = _userHasDraggedToNewLocation({selectedRegionInfo, resetToStorymapLocation, mapState}) // NOTE: __ used to avoid namespace collision due to hoisting

		// if (newIndex !== 0 && !(mapState.selectedRegionInfo.fullLocation))
		// 	return // block changing scenes if we don't have a location

		DataManager.updateLocationForStoryScenes(selectedRegionInfo || mapState.selectedRegionInfo)

		const newScene = DataManager.getStorySceneAtIndex(newIndex)
		recordSceneChangeAsPageviewInGA(newIndex, showStartScreen)

		if ((mapState.storyMapMode || mapState.presenterModeActive) && newScene !== undefined) {
			const newViewport = {
				...mapState.viewport,
				...VIEWPORT_TRANSITION_PROPERTIES,
				...(__userHasDraggedToNewLocation ? {} : newScene.viewport), // If the user is exploration a new location, don't drag them back to the storymap location
			}
			// get visibility for layers for scene, by first setting all layers to false
			// then showing the visible ones in the specific scene
			const newVisibilities = {
				...tableWithSameValue(DataManager.getLayerNames(), false),
				...newScene.visibleLayers
			}
			
			
			
			const stateUpdates = {
				...additionalStateUpdates,
				currentSceneIndex: newIndex,
				..._getNewMapStyleAndVisibleLayers({newVisibilities: newVisibilities, newSceneIndex: newIndex, reduxState: getReduxState()}),
				viewport: newViewport,
				userHasDraggedToNewLocation: __userHasDraggedToNewLocation,
			}
			dispatch(updateMapStateAction(stateUpdates))
			newScene.sceneSpecificGeography && dispatch(changeSelectedGeography(newScene.sceneSpecificGeography))
			dispatch(_updateURLWithCurrentState())
			
			dispatch(openOption(newScene.mapMenuOptionToOpenOnSceneStart))
		}
		
		dispatch(updateToYamlSpecifiedFiltersAndButtonsIfAny(newIndex))
	}
}

const viewportsAreSignificantlyDifferent = ({latitude, longitude, zoom}, viewport) => isSignificantlyDifferent(latitude, viewport.latitude, 0.0001) || isSignificantlyDifferent(longitude, viewport.longitude, 0.0001) || isSignificantlyDifferent(zoom, viewport.zoom, 0.0001)

const _userHasDraggedToNewLocation = ({selectedRegionInfo = false, resetToStorymapLocation = false, mapState, changeTriggeredByAutoBoundingBox = false}) => {
	// EG upon selecting a state, the map will transition to bound that new state, but this is not a deviation from the storymap.
	if (changeTriggeredByAutoBoundingBox) return false
	// If the map is still loading (ie viewport and scenes missing), just keep the status quo
	if (!mapState.viewport || !mapState.viewport.latitude) return mapState.userHasDraggedToNewLocation
	const { currentSceneIndex, _isTransitioning, userHasDraggedToNewLocation } = mapState
	// Get lat and long of CURRENT SCENE (not new scene)
	const currentScene = DataManager.getStorySceneAtIndex(currentSceneIndex)
	if (!currentScene || !currentScene.viewport.latitude) return userHasDraggedToNewLocation

	
	let _userHasDraggedToNewLocation = false
	// SPECIAL CASES:
	// 1. User strayed, then typed in a new location. EXPECTED BEHAVIOUR: map locks onto new location (ignoring the fact things have strayed)
	// 2. User clicks button to re-couple with storymap location. EXPECTED BEHAVIOUR: map zooms to the viewport specified for the current scene
	if (!_isTransitioning && !resetToStorymapLocation && !selectedRegionInfo) {
		_userHasDraggedToNewLocation = viewportsAreSignificantlyDifferent(mapState.viewport, currentScene.viewport)
	}

	return _userHasDraggedToNewLocation
}

const _updateLocationMarkerCoords = () => {
	return (dispatch, getReduxState) => {

		// TODO figure out why doesn't update
		const reduxState = getReduxState()
		const mapState = reduxState.map
		const initialLoadOfMapHasCompleted = initialLoadOfMapHasCompletedSelector(reduxState)
		const { lon = 0, lat = 0 } = mapState.selectedRegionInfo

		geojsonForLocationMarker.features[0].geometry.coordinates = [lon, lat];

		const _map = _getMap(mapState)
		const _locationMarkerSource = _map && _map.getSource('locationMarkerIcon')
		if (_locationMarkerSource) {
			_locationMarkerSource.setData(geojsonForLocationMarker)
		} else if (_map && initialLoadOfMapHasCompleted) {
			_map.addSource(LOCATION_MARKER_ICON_ID, {
				"type": "geojson",
				"data": geojsonForLocationMarker
			});
			_map.addLayer(locationMarkerLayerJSON);
		}
	}
}

const initializeMapFromURL = (mapFromURL = {}) => {
	return (dispatch, getReduxState) => {
		const _currentSceneIndex = mapFromURL.currentSceneIndex || 0
		const urlStateExists = !!mapFromURL.viewport
		const mapStateUpdates = {
			showStartScreen: !urlStateExists,
			initialZoomInFromStartScreenComplete: urlStateExists,
			currentSceneIndex: _currentSceneIndex,
			...mapFromURL,
		}
		dispatch(initializeMapFromURLAction(mapStateUpdates))
	}
}


// EXPORTED Actions (exported for use by UI): *****************************

// ***INITIALIZING ACTIONS:

// NOTE: This is a global-ish Redux action, updating the all the parts of the redux store. Not sure where this should go architecturally...
export const pullReduxStateFromURLIfAny = () => {
	return (dispatch, getReduxState) => {
		const { location } = getReduxState().router
		const reduxStateFromURL = getReduxStateFromURL(location)

		dispatch(initializeMapFromURL(reduxStateFromURL.map))
		dispatch(initializeCartoACSDataFromURL(reduxStateFromURL.cartoACSData))
		dispatch(initializeMapboxLayersFromURL(reduxStateFromURL.mapboxLayers))
	}
}

export const _updateURLWithCurrentState = () => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const _isTransitioning = _isTransitioningSelector(reduxState)
		const showStartScreen = showStartScreenSelector(reduxState)
		if (_isTransitioning || showStartScreen) return // Updating URL during fly-to transitions kills performance/is pointless
		
		const smallReduxStateForURL = smallReduxStateForURLSelector(reduxState)
		dispatch(replace({hash: encodeURIComponent(JSON.stringify(smallReduxStateForURL))}))
	}
}

// Called in componentDidMount to kick off fetching/formatting all storymap info
export const loadTheStoryMap = () => {
	return (dispatch, getReduxState) => {
		// Re-rendering of layers must happen manually, so we call it to sync with the potential URL specifications
		window.addEventListener("resize", () => dispatch(resize())) // Does this work??
		dispatch(resize())

		// Start loading the storymap
		if (config.LOAD_STORYMAP_FROM_REMOTE) {
			fetch('https://rom-scrollytelling-dev-assets.s3.amazonaws.com/sample-storymap.json')
				.then(res => res.json())
				.then(data => { dispatch(_onLoadStorymap(data))})
				.catch( err => {dispatch(_onLoadStorymap(sampleStorymap))})
		} else {
			dispatch(_onLoadStorymap(sampleStorymap)) // load local
		}
	}
}

export const handleMapFirstLoaded = () => {
	return (dispatch, getReduxState) => {
		const mapState = getReduxState().map

		// 1. Subscribe to key map updates
		const _map = _getMap(mapState)
		
		_map.on('styledata', () => {
			dispatch(_updateLocationMarkerCoords())
		})
		_map.on('idle',() => {
			dispatch(initialLoadOfMapHasCompletedAction())
		})

		// 2. Load all the custom icons
		DataManager.getCustomIconsToLoad().forEach(
			iconName => _map.loadImage( // Fetch from /public folder (not s3) to avoid CORS
				`${process.env.PUBLIC_URL}/cori-icons/${iconName}.png`,
				(e, image) => image && _map.addImage(iconName, image)
			))

		// 3. Add carto layers into a combined mapbox style
		DataManager.getMapboxStyleWithCartoLayers(
			_getMap(mapState),
			newDefaultMapStyle => dispatch(updateMapStateAction({
					defaultMapStyle: newDefaultMapStyle,
					..._getNewMapStyleAndVisibleLayers({newVisibilities: mapState.visibleLayers, _newDefaultMapStyle: newDefaultMapStyle, reduxState: getReduxState()})
				}
			))
		)
		
		dispatch(fetchCartoACSSummaryAndDataIfNeeded())
	}
}

export const saveMapRefToRedux = (_mapRef) => {
	return (dispatch, getReduxState) => {
		dispatch(updateMapStateAction({_mapRef}))
	}
}

// ***VIEWPORT UPDATING ACTIONS:
// _onViewportChange wrapper for special case of screen resize
export const resize = () => {
	// console.log('resize: w, h', window.innerWidth * 0.50, window.innerHeight)
	return _onViewportChange({
		width: window.innerWidth * 0.50, // TODO: 50vh vs window.innerWidth /2 ?!!?
		height: window.innerHeight
	});
};

export const _onViewportChange = (viewport, changeTriggeredByAutoBoundingBox = false) => {
	return (dispatch, getReduxState) => {
		const mapState = getReduxState().map
		// console.log('lat,lon,zoom', viewport.latitude, viewport.longitude, viewport.zoom)
		const stateUpdates = {
			userHasDraggedToNewLocation: _userHasDraggedToNewLocation({mapState, changeTriggeredByAutoBoundingBox}),
			viewport: {
				...getReduxState().map.viewport,
				// transitions
				...VIEWPORT_TRANSITION_PROPERTIES,
				...viewport
			},
		}

		dispatch(updateMapStateAction(stateUpdates))
		dispatch(_potentiallySwapBasemaps(viewport.zoom))
	}
}

export const resetToStorymapLocation = () => {
	return (dispatch, getReduxState) => {
		const { currentSceneIndex } = getReduxState().map
		dispatch(_handleSceneChange(currentSceneIndex, undefined, true))
	}
}

export const handleMapTransitionStart = () => {
	return (dispatch, getReduxState) => {
		dispatch(updateMapStateAction({_isTransitioning: true }))
		dispatch(_updateMapStyleOnVisibilityChange())
	}
}

export const handleMapTransitionEnd = () => {
	return (dispatch, getReduxState) => {
		dispatch(updateMapStateAction({_isTransitioning: false, initialZoomInFromStartScreenComplete: true }))
		dispatch(_updateURLWithCurrentState()) // Since we've transitioned to a new location
		dispatch(_updateMapStyleOnVisibilityChange())
	}
}

export const handleTransitionInterrupt = () => {
	return (dispatch, getReduxState) => {
		const mapState = getReduxState().map
		const stateUpdates = {
			_isTransitioning: false,
			initialZoomInFromStartScreenComplete: true,
			userHasDraggedToNewLocation: _userHasDraggedToNewLocation({mapState}),
		}
		dispatch(updateMapStateAction(stateUpdates))
		dispatch(_updateURLWithCurrentState()) // Since we've transitioned to a new location
	}
}

// When a user selects a location, go to the lat long (but don't change zoom etc)
export const _handleGeocoderViewportChange = ({latitude, longitude}) => {
	return (dispatch, getReduxState) => {
		const mapState = getReduxState().map
		const stateUpdates = {
			viewport: {
				...mapState.viewport,
				// transitions
				...VIEWPORT_TRANSITION_PROPERTIES,
				latitude,
				longitude,
			},
		}
		dispatch(updateMapStateAction(stateUpdates))
	}
}

// ***RESPONDING TO DIRECT USER ACTIONS er... ACTIONS:
export const _onUserSetHideLayer = (layerId, isHidden) => {
	return (dispatch, getReduxState) => {
		const {userSetHideLayers} = getReduxState().map
		const stateUpdates = {
			userSetHideLayers: {...userSetHideLayers, [layerId]: isHidden}
		}
		dispatch(updateMapStateAction(stateUpdates))
		dispatch(_updateMapStyleOnVisibilityChange())
	}
}

export const updateSelectedRegionToNewGeography = () => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const selectedGeography = selectedGeographySelector(reduxState)
		const selectedRegionInfo = selectedRegionInfoSelector(reduxState)
		
		dispatch(updateSelectedRegionToNewGeographyAction({
			geoIdForSelectedRegion: getGeoIdForSelectedRegion(selectedRegionInfo.correspondingGeoIdsForNonFIPSGeos, selectedGeography),
		}))
		
		dispatch(_updateLocationMarkerCoords())
		dispatch(_updateMapStyleOnVisibilityChange())
	}
}



export const onSelectACartoRowCommunity = (selectedCommunityRow, options) => {
	return (dispatch, getReduxState) => {
		const {shouldClearLastClickedLayersInfo = true, shouldZoomToNewLocation = true} = options || {}
		if (selectedCommunityRow) {
			const reduxState = getReduxState()
			const selectedRegionACSDataRow = selectedRegionACSDataRowSelector(reduxState)
			const aSelectedRegionAlreadyExists = !!Object.keys(selectedRegionACSDataRow).length
			
			const {lat, lon, namelsad, geoid} = selectedCommunityRow
			const stateName = AdminBoundsDataManager.stateNameFromFIPS(geoid)
			const fullLocation = namelsad + ', ' + stateName + ', United States'

			const _selectedRegionInfo = {
				lat: Number(lat), lon: Number(lon),
				stateName: stateName,
				fullLocation: fullLocation,
				geoIdForSelectedRegion: geoid
			}
			
			const isSelectedRegion = !(DataManager.clickingACommunityOnTheMapShouldCompareIt() && aSelectedRegionAlreadyExists)
			
			const stateUpdates = isSelectedRegion
				? {selectedRegionInfo: _selectedRegionInfo}
				: {comparisonRegionInfo: _selectedRegionInfo}
				
			dispatch(updateMapStateAction(stateUpdates))
			dispatch(selectCommunityAndFetchItsGeoIdLookupData(_selectedRegionInfo, isSelectedRegion, false))
			shouldClearLastClickedLayersInfo && dispatch(clearLastClickedLayersInfo())
			dispatch(_updateLocationMarkerCoords())
			dispatch(addUSStateToFilterWithin(stateName))
			shouldZoomToNewLocation && dispatch(_onViewportChange({
				latitude: +lat,
				longitude: +lon,
				zoom: COUNTY_ZOOM_TO_LEVEL,
			}))
		}
	}
}

const onSelectACartoRowCommunityByClickingOnMap = (selectedCommunityRow) => {
	return onSelectACartoRowCommunity(selectedCommunityRow, {
		shouldClearLastClickedLayersInfo: false,
		shouldZoomToNewLocation: false,
	})
}

export const _handleSelectLocationFromGeocoder = ({result}) => {
	return (dispatch, getReduxState) => {
		const [lon, lat] = result.center
		const fullLocation = result['place_name']
		// Mapbox lists all the contexts you're within (country, region, place, etc). US State is a 'region'
		// For sub-state queries, we can extract the state from result.context. For direct searches of states, pull it from result.text
		const stateName = result.id.startsWith('region') ? result.text : result.context.find(o => o.id.includes('region')).text

		const selectedRegionInfo = {
			lat: Number(lat), lon: Number(lon),
			stateName: stateName, fullLocation: fullLocation
		}
		
		// TODO: save region here? That way we can simplify the below function to JUST fetch metadata

		dispatch(selectCommunityAndFetchItsGeoIdLookupData(selectedRegionInfo))
	}
};

export const selectCommunityAndFetchItsGeoIdLookupData = (selectedRegionInfo, isSelectedRegion = true, updateViewport = true) => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const selectedGeography = selectedGeographySelector(reduxState)
		
		// TODO: maybe save selectedRegionInfo BEFORE fetch in case it's slow?
		
		dispatch(getCartoGeoIdForLatLon(selectedRegionInfo, correspondingGeoIdsForNonFIPSGeos => {
				const regionInfoKey = `${isSelectedRegion ? 'selected' : 'comparison'}RegionInfo`
				const stateUpdates = {
					[regionInfoKey]: {
						...selectedRegionInfo,
						geoIdForSelectedRegion: getGeoIdForSelectedRegion(correspondingGeoIdsForNonFIPSGeos, selectedGeography),
						correspondingGeoIdsForNonFIPSGeos,
					}
				}
				dispatch(updateMapStateAction(stateUpdates))
				
				isSelectedRegion && dispatch(_updateLocationMarkerCoords())
				dispatch(addUSStateToFilterWithin(selectedRegionInfo.stateName))
				dispatch(_updateMapStyleOnVisibilityChange())
				updateViewport && dispatch(_onViewportChange({latitude: selectedRegionInfo.lat, longitude: selectedRegionInfo.lon, zoom: COUNTY_ZOOM_TO_LEVEL}))
			}
		))
	}
}


export const onCloseStartScreen = () => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const currentSceneIndex = currentSceneIndexSelector(reduxState)
		
		recordSceneChangeAsPageviewInGA(currentSceneIndex, false) // Because we technically don't change scene from startScreee-> scene0
		dispatch(updateMapStateAction({showStartScreen: false}))
	}
}

export const onSubmitStateSelection = (updatedSelectedUSStatesToFilterWithin) => {
	return (dispatch, getReduxState) => {
		// 0. Submit US State Names
		dispatch(updateUSStatesToFilterWithinAction(updatedSelectedUSStatesToFilterWithin))
		
		// 1. Begin any potential fetches
		dispatch(fetchCartoACSSummaryAndDataIfNeeded())
		
		// 2. Zoom to state(s) bounding box
		const reduxState = getReduxState()
		const bounds = getBoundForStates(reduxState)
		if (bounds) {
			dispatch(mapFitBounds(bounds))
		} else { // if, for some reason, Turf fails
			const selectedUSStatesToFilterWithin = selectedUSStatesToFilterWithinSelector(reduxState)
			const mostRecentChosenState = selectedUSStatesToFilterWithin[selectedUSStatesToFilterWithin.length - 1]
			const [stateLat, stateLon] = LAT_LON_FOR_US_STATES[mostRecentChosenState]
			const changeTriggeredByAutoBoundingBox = true
			dispatch(_onViewportChange({latitude: stateLat, longitude: stateLon, zoom: STATE_ZOOM_TO_LEVEL}, changeTriggeredByAutoBoundingBox))
		}
	}
}

export const _updateMapStyleOnVisibilityChange = (newVisibilities = {}) => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		dispatch(updateMapStateAction(_getNewMapStyleAndVisibleLayers({newVisibilities, reduxState})))
		dispatch(_updateURLWithCurrentState)
	}
}


// TODO: make this an actual action creator (or just use selectors more carefully)
export const doFieldFilterBoundsUpdateInMapRedux = (fieldFilterUpdates) => {
	return (dispatch, getReduxState) => {
		const mapState = getReduxState().map
		const dynamicMapboxFilters = {
			dynamicMapboxFilters: {
				...mapState.dynamicMapboxFilters,

				fieldFilterBounds: {
					...mapState.dynamicMapboxFilters.fieldFilterBounds,
					...(fieldFilterUpdates.updatedFieldFilterBounds)
				},
			}
		}

		dispatch(updateMapStateAction(dynamicMapboxFilters))
		dispatch(_updateMapStyleOnVisibilityChange())
	}
}

export const _toggleSatelliteLayer = () => {
	return (dispatch, getReduxState) => {
		const mapState = getReduxState().map
		const {showSatelliteLayer} = mapState

		const stateUpdates = {
			showSatelliteLayer: !mapState.showSatelliteLayer
		}
		dispatch(updateMapStateAction(stateUpdates))

		const z = DataManager.getZoomLevelToSwapBasemaps()
		const currentBase = (mapState.viewport.zoom > z) ? BASE_MAP_LAYERS.DETAILED : BASE_MAP_LAYERS.BASIC

		if (showSatelliteLayer) {
			DataManager.swapBaseLayers(_getMap(mapState), BASE_MAP_LAYERS.SATELLITE, currentBase)
		} else {
			DataManager.swapBaseLayers(_getMap(mapState), currentBase, BASE_MAP_LAYERS.SATELLITE)
		}

		// force a viewport update
		dispatch(_onViewportChange(mapState.viewport))
	}
}

export const updateShowPrimaryFilter = () => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const numActiveFilters = numActiveFiltersSelector(reduxState)
		const atLeastOneFilterActiveNow = !!numActiveFilters
		
		const stateUpdates = {
			showPrimaryFilter: atLeastOneFilterActiveNow,
		}
		
		dispatch(updateMapStateAction(stateUpdates))
		dispatch(_updateMapStyleOnVisibilityChange())
	}
}



export const toggleShowPrimaryFilter = () => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const showPrimaryFilter = showPrimaryFilterSelector(reduxState)
		
		const stateUpdates = {
			showPrimaryFilter: !showPrimaryFilter,
		}
		
		dispatch(updateMapStateAction(stateUpdates))
		dispatch(_updateMapStyleOnVisibilityChange())
	}
}

const clearLastClickedLayersInfo = () => {
	return (dispatch, getReduxState) => {
		const stateUpdates = {
			clickedLayersInfo: null,
		}
		dispatch(updateMapStateAction(stateUpdates))
	}
}

export const updateToYamlSpecifiedFiltersAndButtonsIfAny = (newSceneIndex = undefined) => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const fieldExtents = fieldExtentsSelector(reduxState)
		const sceneIndex = newSceneIndex === undefined ? currentSceneIndexSelector(reduxState) : newSceneIndex
		
		const communitySummaryFields = DataManager.communitySummaryFieldsAtIndex(sceneIndex)
		communitySummaryFields.forEach(yamlSpecifiedFieldFilterInfo => {
			if (Object.keys(fieldExtents).length) {
				// SEED WITH BOOLS REGARDLESS (how to prevent an unwanted overwrite?)
				dispatch(seedButtonToggleStatusArrayForFieldAction(yamlSpecifiedFieldFilterInfo))
				
				const {buttonIsActiveOnSceneStart = []} = yamlSpecifiedFieldFilterInfo
				buttonIsActiveOnSceneStart.forEach((buttonIsActive, buttonIndex) => {
					if (buttonIsActive) {
						// SET FILTERS
						dispatch(updateFieldsBoundsAfterYamlSpecifiedFilterButtonPress(yamlSpecifiedFieldFilterInfo, buttonIndex))
					}
				})
			}
		})
	}
}

export const _handleSceneChange = (newIndex, additionalStateUpdates = {}, resetToStorymapLocation) => {
	return (dispatch, getReduxState) => {
		dispatch(clearLastClickedLayersInfo())
		dispatch(_changeScene(newIndex, additionalStateUpdates, resetToStorymapLocation))
	}
}

// *** CLICK EVENT ACTIONS
export const onMapClick = event => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const mapState = mapSelector(reduxState)
		const selectedGeography = selectedGeographySelector(reduxState)
		
		const { features, lngLat } = event
		const clickedCartoFeatures = filterToJustCartoFeatures(features)
		
		// Flag layers (and their fields) which are being visualized
		const { selectedRegionInfo = {} } = mapState
		
		DataManager.updateLocationForStoryScenes(selectedRegionInfo)
		
		const stateUpdates = {
			clickedLayersInfo: clickedCartoFeatures && clickedCartoFeatures.length && { lngLat, clickedCartoFeatures, visualizedLayersAndTheirField: {} },
		}
		dispatch(updateMapStateAction(stateUpdates))
		
		const clickedACSGeography = clickedCartoFeatures && clickedCartoFeatures.find(({layer: {id}}) => selectedGeography.cartoLayerId === id)
		if (clickedACSGeography) {
			dispatch(onSelectACartoRowCommunityByClickingOnMap(clickedACSGeography.properties))
			dispatch(_updateMapStyleOnVisibilityChange())
		}
	}
}
const LAYER_IDS_TO_EXCLUDE_FROM_POPUP = [
	'state_pg',
]
const filterToJustCartoFeatures = (features) => {
	const existingIds = []
	return features &&
		features.filter(({layer}) => { // remove non data layers and duplicates
			const { id } = layer
			const isActiveLayer = !LAYER_IDS_TO_EXCLUDE_FROM_POPUP.includes(id) && DataManager.includesLayerId(id) && !existingIds.includes(id)
			existingIds.push(id)
			return isActiveLayer
		})
}

const setFeatureState = ({id, layer, source, sourceLayer}, state = {hover: true}) => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		const _mapRef = _mapRefSelector(reduxState)
		const currentMap = _getMap({_mapRef})
		if (!layer || !currentMap) return
		
		currentMap.setFeatureState && currentMap.setFeatureState({
			source,
			sourceLayer,
			id,
		}, state)
	}
}

const updateHoveredLayersInfo = (srcEvent, hoveredCartoFeatures) => {
	return (dispatch, getReduxState) => {
		const mouseHoverCoords = srcEvent && srcEvent.pageX && srcEvent.pageY
			? { x: Math.round(srcEvent.pageX), y: Math.round(srcEvent.pageY)}
			: { x: null, y: null }
		
		const stateUpdates = {
			hoveredLayersInfo: hoveredCartoFeatures
				&& hoveredCartoFeatures.length
				&& {
					mouseHoverCoords,
					hoveredCartoFeatures,
				}
		}
		dispatch(updateMapStateAction(stateUpdates))
	}
}

export const onMapHover = event => {
	return (dispatch, getReduxState) => {
		const reduxState = getReduxState()
		
		// 1. Clear old hovered features
		const hoveredLayersInfo = hoveredLayersInfoSelector(reduxState)
		if (hoveredLayersInfo.hoveredCartoFeatures && Object.keys(hoveredLayersInfo.hoveredCartoFeatures).length) {
			hoveredLayersInfo.hoveredCartoFeatures.forEach(hoveredFeature => dispatch(setFeatureState(hoveredFeature, {hover: false})))
		}
		
		// 2. Store hovered features in Redux
		const { features, srcEvent } = event
		const hoveredCartoFeatures = filterToJustCartoFeatures(features)
		dispatch(updateHoveredLayersInfo(srcEvent, hoveredCartoFeatures))
		
		// 3. Mark new features as hovered
		hoveredCartoFeatures && hoveredCartoFeatures.length && hoveredCartoFeatures.forEach(hoveredFeature => dispatch(setFeatureState(hoveredFeature, {hover: true})))
	}
}

// Wraps _updateURLWithCurrentState since URL update coordination should stay out of the UI
export const handleMouseUpOnMap = () => _updateURLWithCurrentState()

export const handlePopupClose = () => {
	return (dispatch, getReduxState) => {
		dispatch(updateMapStateAction({clickedLayersInfo: false}))
		dispatch(_updateMapStyleOnVisibilityChange())
	}
}


// PROTOTYPE DISPATCH FUNCTION
// return (dispatch, getReduxState) => {
// 	const mapState = getReduxState().map
// 	const stateUpdates = {}
// 	dispatch(updateMapStateAction(stateUpdates))
// }
